import { withTheme } from '@emotion/react';
import React, { MouseEvent, useRef, useState } from 'react';
import { KeyCodes, TabIndex } from '@model/common';
import { BaseHero, ImageContainer } from '@components/common/hero/base/BaseHero';
import styled from '@emotion/styled';
import { TestId } from '@components/test-ids';
import { ImageGalleryModalController, ImageGalleryModalMobile } from '@components/common/image-gallery';
import { Image } from '@model/common/image';
import { breakpoints, isMobile, isTablet, mq } from '@styles/breakpoints';
import { getImageUrl } from '@util/images';
import { getCrossfadeImages } from '@util/product';
import { SkeletonElement, SkeletonVariant } from '@components/common/skeleton-loading';
import { HeroGallery } from '@components/common/hero-gallery';

export interface ProductHeroProps {
  hero?: Image;
  gallery?: Array<Image>;
  isGhosting?: boolean;
}

const LimitedHeightHero = styled(BaseHero)({
  height: '50vh',
  [ImageContainer as any]: {
    paddingBottom: '50vh',
    position: 'absolute'
  },
  [mq.small]: {
    height: '50vh' // overwrites base hero
  }
});

export const ProductHeroComponent = ({ hero, gallery, isGhosting }: ProductHeroProps) => {
  const galleryImages = gallery!.map((image: Image) => ({
    thumbnail: getImageUrl(image.url, breakpoints.small),
    original: getImageUrl(image.url, breakpoints.medium)
  }));
  const [showGallery, setShowGallery] = useState<boolean>(false);
  const toggleGallery = (show?: boolean) => {
    setShowGallery(typeof show !== 'undefined' ? show : !showGallery);
  };

  // prevent drag events triggering click
  const mousePos = useRef({ x: 0 });
  const trackMousePosition = (e: MouseEvent) => {
    mousePos.current.x = e.screenX;
  };
  const heroGalleryClick = (e: MouseEvent) => {
    const delta = Math.abs(e.screenX - mousePos.current.x);

    if (delta < 10) {
      toggleGallery();
    }
  };
  const handleOnKeyPress = (event: any) => {
    const { keyCode, which } = event;
    const key = keyCode || which;
    if (key === KeyCodes.SPACE || key === KeyCodes.ENTER) toggleGallery();
  };

  const heroImage = hero?.url;

  if (isGhosting || !heroImage) {
    return <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={'50vh'} />;
  }

  return (
    <>
      {(isMobile() || isTablet()) && !!gallery?.length ? (
        <>
          <div
            onClick={heroGalleryClick}
            onKeyPress={handleOnKeyPress}
            onMouseDown={trackMousePosition}
            role="button"
            tabIndex={TabIndex.ZERO}
          >
            {hero && <HeroGallery images={getCrossfadeImages(hero, gallery)} />}
          </div>
          <ImageGalleryModalMobile
            galleryImages={galleryImages}
            onDismiss={() => setShowGallery(false)}
            showGallery={showGallery}
          />
        </>
      ) : (
        <LimitedHeightHero
          testId={TestId.productHero.main}
          loading={'eager'}
          src={heroImage}
          renderBottom={!!gallery?.length && <ImageGalleryModalController gallery={gallery} />}
        />
      )}
    </>
  );
};

ProductHeroComponent.defaultProps = {
  gallery: []
};

export const ProductHero = withTheme(ProductHeroComponent);
